import { Link } from 'react-router-dom';
import { Building2, Users, Shield, Zap, Wrench, Trash2, Paintbrush, Home, MapPin, Heart } from 'lucide-react';

function Services() {
  const publicSectorServices = [
    'Rapid Deployment & Emergency Response',
    'Base Operations & DoD Support',
    'Department of Justice & Federal Facility Support',
    'Construction & Site Development',
    'Temporary Housing & Camp Setup',
    'Security & Access Control',
    'Logistics & Transportation Support',
    'Environmental & Infrastructure Services',
    'Temporary Power',
    'Defense and Aerospace',
    'Healthcare Facilities (VA & Military Medical)',
    'Exterior Services (Roofing, Facades, Site Work)'
  ];

  const privateSectorServices = [
    { name: 'Civil & Commercial Construction', path: '/private-sector/civil-commercial-construction' },
    { name: 'Project Management', path: '/private-sector/project-management' },
    { name: 'Demolition & Site Preparation', path: '/private-sector/demolition-site-preparation' },
    { name: 'Plumbing, Electrical & Essential Systems', path: '/private-sector/plumbing-electrical-systems' },
    { name: 'Interior Finishes & Custom Detailing', path: '/private-sector/interior-finishes' },
    { name: 'Structural & Exterior Works', path: '/private-sector/structural-exterior' },
    { name: 'Nationwide Roll Outs', path: '/private-sector/nationwide-rollouts' },
    { name: 'Tenant Build Outs', path: '/private-sector/tenant-buildouts' },
    { name: 'Store Decommissioning', path: '/private-sector/store-decommissioning' }
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">What We Offer</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Comprehensive Facility & <span className="text-gold">Construction Services</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto">
            Serving both Public and Private sector clients with specialized expertise and unwavering commitment to excellence
          </p>
        </div>
      </section>

      {/* Public Sector Section */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 brass-particles"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <Shield className="w-16 h-16 text-gold" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              <span className="text-gold">Public Sector</span> Services
            </h2>
            <p className="text-gray-300 text-xl max-w-3xl mx-auto mb-8">
              Specialized government contracting services for federal, state, and municipal projects
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Rapid Deployment & Emergency Response */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_diversified-web/artifacts/lorwseaf_image.jpeg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Zap className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Rapid Deployment & Emergency Response
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">48-hour nationwide deployment for critical facilities and emergency operations</p>
              </div>
            </Link>

            {/* Base Operations & DoD Support */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1708733818543-8a895c354151?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Shield className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Base Operations & DoD Support
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Comprehensive support services for military installations and defense operations</p>
              </div>
            </Link>

            {/* Department of Justice & Federal Facilities */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_diversified-web/artifacts/8h83oyxr_image.jpeg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Building2 className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Department of Justice & Federal Facilities
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Construction and maintenance services for federal buildings and secure facilities</p>
              </div>
            </Link>

            {/* Construction & Site Development */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1541888946425-d81bb19240f5?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Building2 className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Construction & Site Development
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Full-scale construction and site development for government projects</p>
              </div>
            </Link>

            {/* Temporary Housing & Camp Setup */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_diversified-web/artifacts/l92ff6lk_image.png)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Home className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Temporary Housing & Camp Setup
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Modular housing and complete camp facilities for military and emergency operations</p>
              </div>
            </Link>

            {/* Security & Access Control */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_diversified-web/artifacts/kf5nfdt2_image.png)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Shield className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Security & Access Control
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Advanced security systems and access control for sensitive government facilities</p>
              </div>
            </Link>

            {/* Logistics & Transportation Support */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_diversified-web/artifacts/4ssmbikq_image.png)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Wrench className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Logistics & Transportation Support
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Comprehensive logistics and transportation services for government operations</p>
              </div>
            </Link>

            {/* Environmental & Infrastructure Services */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_diversified-web/artifacts/2va1djob_image.jpeg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Building2 className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Environmental & Infrastructure Services
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Environmental remediation and critical infrastructure support services</p>
              </div>
            </Link>

            {/* Temporary Power */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_diversified-web/artifacts/w3zqf7cy_image.jpeg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Zap className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Temporary Power
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Emergency and temporary power generation for critical operations</p>
              </div>
            </Link>

            {/* Defense and Aerospace */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_diversified-web/artifacts/r1n130ok_image.jpeg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Shield className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Defense and Aerospace
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Specialized construction and support for defense and aerospace facilities</p>
              </div>
            </Link>

            {/* Healthcare Facilities (VA & Military Medical) */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_diversified-web/artifacts/5bqj55e3_image.jpeg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Heart className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Healthcare Facilities (VA & Military Medical)
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Construction and renovation of VA hospitals and military medical facilities</p>
              </div>
            </Link>

            {/* Exterior Services (Roofing, Facades, Site Work) */}
            <Link
              to="/government-contracting"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_diversified-web/artifacts/9onms9n8_image.jpeg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Home className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Exterior Services (Roofing, Facades, Site Work)
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Complete exterior services for government and military facilities</p>
              </div>
            </Link>
          </div>

          <div className="text-center mt-12 relative z-20">
            <Link to="/government-contracting" className="inline-block text-gold font-bold text-lg uppercase tracking-wider hover:text-gold-dark transition-colors">
              View All Public Sector Services →
            </Link>
          </div>
        </div>
      </section>

      {/* Private Sector Section */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 brass-particles"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <Users className="w-16 h-16 text-gold" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              <span className="text-gold">Private Sector</span> Services
            </h2>
            <p className="text-gray-300 text-xl max-w-3xl mx-auto mb-8">
              Commercial construction and specialized services for businesses nationwide
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Link
              to="/private-sector/civil-commercial-construction"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1599995903128-531fc7fb694b?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Building2 className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Civil & Commercial Construction
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Large-scale civil works and premier commercial developments</p>
              </div>
            </Link>

            <Link
              to="/private-sector/project-management"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Wrench className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Project Management
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">End-to-end project management ensuring on-schedule delivery</p>
              </div>
            </Link>

            <Link
              to="/private-sector/demolition-site-preparation"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(/images/demolition_site_prep_collage.jpg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Trash2 className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Demolition & Site Preparation
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Safe, efficient demolition and site preparation services</p>
              </div>
            </Link>

            <Link
              to="/private-sector/plumbing-electrical-systems"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1541888946425-d81bb19240f5?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Zap className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Plumbing, Electrical & Systems
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Vital building systems installed to highest safety standards</p>
              </div>
            </Link>

            <Link
              to="/private-sector/interior-finishes"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1482731215275-a1f151646268?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Paintbrush className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Interior Finishes & Custom Detailing
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Premium finishing services creating refined, sophisticated spaces</p>
              </div>
            </Link>

            <Link
              to="/private-sector/structural-exterior"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1535732759880-bbd5c7265e3f?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Home className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Structural & Exterior Works
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Foundation for strength, safety, and lasting appeal</p>
              </div>
            </Link>

            <Link
              to="/private-sector/nationwide-rollouts"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(/images/nationwide_rollouts_collage.jpg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <MapPin className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Nationwide Roll Outs
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Multi-location construction for brands expanding nationwide</p>
              </div>
            </Link>

            <Link
              to="/private-sector/tenant-buildouts"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.pexels.com/photos/13068378/pexels-photo-13068378.jpeg?auto=compress&cs=tinysrgb&fit=crop&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Building2 className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Tenant Build Outs
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Custom interior construction for your business needs</p>
              </div>
            </Link>

            <Link
              to="/private-sector/exterior-interior-remodels"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(/images/remodel_collage.jpg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Home className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Exterior/Interior Remodels
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Complete renovation services transforming existing spaces</p>
              </div>
            </Link>

            <Link
              to="/private-sector/healthcare-facilities"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.pexels.com/photos/263402/pexels-photo-263402.jpeg?auto=compress&cs=tinysrgb&fit=crop&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Heart className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Healthcare Facilities
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Medical facilities construction and specialized healthcare infrastructure</p>
              </div>
            </Link>

            <Link
              to="/private-sector/exterior-services"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(/images/exterior_services_collage.jpg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Home className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Exterior Services & Roofing
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Complete roofing systems, facades, site work, and exterior construction</p>
              </div>
            </Link>

            <Link
              to="/private-sector/store-decommissioning"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(/images/store_decommissioning_collage.jpg)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
              <div className="relative z-10 p-8">
                <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                  <Trash2 className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Store Decommissioning
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">Professional facility closure, asset recovery, and site restoration services</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Ready to <span className="text-gold">Get Started?</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            Contact us to discuss how we can support your next project
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Services;